/**
 * auras.ts — MFix8.5 per-ship aura aggregation.
 *
 * Auras are per-ship buffs that apply to friendly ships within a fixed
 * range of an emitter. Each emitter chassis carries an `aura` reference
 * (string name) on its `ShipTemplate`; the aura's bonuses come from
 * `state.auras`, loaded from the `Auras` sheet on Ships.xlsx.
 *
 * Bonus aggregation rules (when multiple emitters affect the same
 * consumer):
 *   - Multiplicative bonuses (`shieldMult`, `shieldRegenMult`,
 *     `damageMult`, `speedMult`) MULTIPLY together. Two emitters with
 *     1.3 and 1.5 shieldMult give 1.95 to a consumer in range of both.
 *   - Additive bonuses (`stealthBonus`, `detectionBonus`) SUM.
 *
 * Eligibility (per emitter):
 *   - Emitter must be alive (`currentHull > 0`).
 *   - Emitter must be in-system (not mid-jump, `systemId !== null`).
 *   - Emitter and consumer must share `systemId`.
 *   - Emitter and consumer must share `ownerEmpireId` (friendly-only).
 *   - Squared distance between emitter (sx, sy) and consumer (sx, sy)
 *     must be ≤ aura.range² (in 0..1 in-coord units).
 *   - Emitter is its own ally — a Bulwark gets its own buff.
 *
 * Edge cases:
 *   - Mid-jump consumer (`ship.systemId === null`) → no bonuses (no
 *     position to compare). Aura buffs don't follow ships into
 *     hyperspace.
 *   - EMP-disabled emitter → bonuses still apply. Aura projection is
 *     passive; EMP only stops active firing/movement, not field
 *     emission. Revisitable in v3M11 polish if playtest shows it's
 *     overpowered.
 *   - Cloaked emitter → bonuses still apply. Cloak hides from
 *     detection; it doesn't suppress the aura field.
 *
 * Performance: per-consumer call walks an emitter SHORTLIST cached
 * per (state, tick) — typically 0–3 entries — so each call is O(E)
 * with E = active emitter count, not O(N) over all ships. The
 * shortlist itself is built once per tick on first access via a
 * module-level WeakMap; commands.test.ts ran 20 000 ticks with the
 * naive O(N) walk costing ~10× more wall time, so the shortlist is
 * load-bearing.
 *
 * Cache invalidation:
 *   - Different `state` identity (per-test) → fresh shortlist.
 *   - Same state, advanced `state.tick` → fresh shortlist.
 *   - Within a single tick: the shortlist is stable. Liveness
 *     (`currentHull > 0`, `systemId !== null`) and position checks
 *     are re-read live from each emitter ShipInstance at call time,
 *     so an emitter that takes lethal damage mid-combat stops
 *     buffing future calls in the same tick.
 */

import type {
  AuraDef,
  GameState,
  ShipInstance,
} from '../state/types/index';

/**
 * Aggregated aura bonuses for a single consumer ship. Multiplier
 * fields default to 1.0 (no effect); additive fields default to 0.
 */
export interface AuraBonuses {
  shieldMult: number;
  shieldRegenMult: number;
  damageMult: number;
  speedMult: number;
  stealthBonus: number;
  detectionBonus: number;
}

/** No-aura singleton — returned when state.auras is empty / consumer has no eligible emitters. */
const NO_AURA: AuraBonuses = Object.freeze({
  shieldMult: 1,
  shieldRegenMult: 1,
  damageMult: 1,
  speedMult: 1,
  stealthBonus: 0,
  detectionBonus: 0,
});

/**
 * Per-(state, tick) cache of "every ship that could conceivably project
 * an aura this tick". Stored as a list of {ship, def} pairs so the
 * lookup-side per-emitter work (empire/template/aura name resolution)
 * is paid once per tick instead of once per consumer call.
 *
 * WeakMap keyed by state so per-test fixtures GC cleanly without us
 * needing an explicit reset between describe blocks.
 */
interface EmitterEntry {
  ship: ShipInstance;
  def: AuraDef;
}
interface EmitterCache {
  tick: number;
  emitters: ReadonlyArray<EmitterEntry>;
}
const emitterShortlistByState = new WeakMap<GameState, EmitterCache>();
const EMPTY_EMITTERS: ReadonlyArray<EmitterEntry> = [];

function getEmittersForTick(state: GameState): ReadonlyArray<EmitterEntry> {
  const cached = emitterShortlistByState.get(state);
  if (cached && cached.tick === state.tick) return cached.emitters;
  if (!state.auras || state.auras.size === 0) {
    emitterShortlistByState.set(state, { tick: state.tick, emitters: EMPTY_EMITTERS });
    return EMPTY_EMITTERS;
  }
  const list: EmitterEntry[] = [];
  for (const ship of state.ships.values()) {
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl || !tmpl.aura) continue;
    const def = state.auras.get(tmpl.aura);
    if (!def) continue;
    list.push({ ship, def });
  }
  emitterShortlistByState.set(state, { tick: state.tick, emitters: list });
  return list;
}

/**
 * Compute aggregate aura bonuses for `consumer`. Walks `state.ships`
 * for every emitter whose template carries an `aura` reference and
 * checks the eligibility chain (alive, in-system, friendly, in range).
 * Multiplicative bonuses compound; additive bonuses sum. Returns the
 * NO_AURA singleton when no eligible emitters are found (cheap fast
 * path for the common case).
 */
export function auraBonusesForShip(
  state: GameState,
  consumer: ShipInstance,
): AuraBonuses {
  if (consumer.systemId === null) return NO_AURA;
  const emitters = getEmittersForTick(state);
  if (emitters.length === 0) return NO_AURA;

  let shieldMult = 1;
  let shieldRegenMult = 1;
  let damageMult = 1;
  let speedMult = 1;
  let stealthBonus = 0;
  let detectionBonus = 0;
  let foundAny = false;

  const consumerEmpireId = consumer.ownerEmpireId;
  const consumerSystemId = consumer.systemId;

  for (const entry of emitters) {
    const emitter = entry.ship;
    if (emitter.ownerEmpireId !== consumerEmpireId) continue;
    if (emitter.systemId !== consumerSystemId) continue;
    if (emitter.currentHull <= 0) continue;
    const def = entry.def;
    const dx = emitter.sx - consumer.sx;
    const dy = emitter.sy - consumer.sy;
    if (dx * dx + dy * dy > def.range * def.range) continue;
    foundAny = true;
    shieldMult *= def.shieldMult;
    shieldRegenMult *= def.shieldRegenMult;
    damageMult *= def.damageMult;
    speedMult *= def.speedMult;
    stealthBonus += def.stealthBonus;
    detectionBonus += def.detectionBonus;
  }

  if (!foundAny) return NO_AURA;
  return {
    shieldMult,
    shieldRegenMult,
    damageMult,
    speedMult,
    stealthBonus,
    detectionBonus,
  };
}

/**
 * Convenience: just the consumer's aura definition lookup. Used by
 * detail-panel rendering to surface "Aura: Bulwark" on emitter ships.
 * Returns null when the ship's template carries no `aura` field, when
 * the lookup misses (data drift), or when state.auras is unset.
 */
export function emittedAuraDef(
  state: GameState,
  ship: ShipInstance,
): AuraDef | null {
  if (!state.auras || state.auras.size === 0) return null;
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return null;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl || !tmpl.aura) return null;
  return state.auras.get(tmpl.aura) ?? null;
}
